import { fingerFor, type Bundle, type Lesson } from "@otto/lehrgang";

/** Welche Zusatztaste die Lektion für diesen Großbuchstaben verlangt. */
export type Modifier =
  | { readonly kind: "capsLock" }
  | { readonly kind: "shift"; readonly side: "links" | "rechts" };

/**
 * Der Weg zum Großbuchstaben, aus Lektion und Fingerzuordnung zusammengesetzt.
 *
 * Zwei Angaben aus den Daten, keine Regel im Code:
 *
 * - **welche** Zusatztaste sagt die Lektion (`uppercaseWith`);
 * - **welche Seite** sagt der Finger, der den kleinen Buchstaben holt. Die
 *   MERKE-Regel von Heftseite 24 – Buchstabe links, Umschalttaste rechts –
 *   steckt im „linker …“/„rechter …“ seines Namens, und das ist im ganzen Bundle
 *   Konvention.
 *
 * `null`, wo nichts zu sagen ist: kein Großbuchstabe, oder eine Lektion, die den
 * Weg nicht vorschreibt.
 *
 * Vorher trugen die Namen der Großbuchstaben-Finger den Modifikator selbst
 * („linker kleiner Finger + rechte Umschalttaste“). Das ging gut, solange nur
 * Heft 5a einen Weg kannte – in Lektion 10 behauptete es die Umschalttaste,
 * obwohl sie dort ein Vertipper ist.
 */
export function modifierFor(
  bundle: Bundle,
  lesson: Lesson,
  expected: string | null,
): Modifier | null {
  if (expected === null || lesson.uppercaseWith === undefined) return null;
  if (expected === expected.toLowerCase()) return null;
  if (lesson.uppercaseWith === "capsLock") return { kind: "capsLock" };

  const finger = fingerFor(bundle, expected.toLowerCase());
  if (!finger) return null;
  return { kind: "shift", side: finger.name.startsWith("linker") ? "rechts" : "links" };
}

/** Wie der Modifikator im Fingerhinweis genannt wird. */
export function modifierName(modifier: Modifier | null): string | null {
  if (modifier === null) return null;
  return modifier.kind === "capsLock"
    ? "Feststelltaste"
    : `${modifier.side === "links" ? "linke" : "rechte"} Umschalttaste`;
}
